import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, MessageSquare, Clock, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const Contact = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const orderNumber = searchParams.get("order") || "";
  const deliverableType = searchParams.get("type") || "";

  const defaultSubject = orderNumber
    ? `${deliverableType ? `${deliverableType.charAt(0).toUpperCase() + deliverableType.slice(1)} Feedback` : "Feedback"} — Order #${orderNumber}`
    : "";

  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;

    setSending(true);
    const { error } = await supabase.functions.invoke("send-contact-email", {
      body: {
        subject: subject.trim(),
        message: message.trim(),
        from_email: user?.email || "unknown@customer.com",
        from_name: user?.user_metadata?.full_name || user?.email || "Customer",
      },
    });

    if (error) {
      toast({ title: "Failed to send", description: "Please try again or email us directly.", variant: "destructive" });
    } else {
      setSent(true);
      toast({ title: "Message sent!", description: "We'll get back to you within 24–48 hours." });
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Contact Us"
        description="Questions about your custom song order? Get in touch with the Your Moment Song team. We typically respond within 24 hours."
        canonical="https://yourmomentsong.com/contact"
        url="https://yourmomentsong.com/contact"
      />
      <Header />
      <main className="container mx-auto px-4 py-20 md:py-28">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <MessageSquare className="w-8 h-8 text-primary" aria-hidden="true" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
            Get in Touch
          </h1>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Have a question about your song, need help with an order, or just want to say hello? We'd love to hear from you.
          </p>
        </div>

        <div className="max-w-md mx-auto space-y-8">
          {sent ? (
            <div className="bg-card border border-border rounded-2xl p-8 text-center space-y-4">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle className="w-7 h-7 text-green-600" aria-hidden="true" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-foreground">Message Sent!</h2>
              <p className="text-muted-foreground text-sm">
                We typically reply within 24 to 48 hours. Keep an eye on your inbox.
              </p>
              <Button variant="outline" onClick={() => { setSent(false); setMessage(""); }}>
                Send Another Message
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl p-6 space-y-5">
              <div className="space-y-2">
                <label htmlFor="subject" className="text-sm font-medium text-foreground">Subject</label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="What's this about?"
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="message" className="text-sm font-medium text-foreground">Message</label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us how we can help…"
                  className="min-h-[140px]"
                  maxLength={2000}
                  required
                />
              </div>
              {user?.email && (
                <p className="text-xs text-muted-foreground">
                  Sending as <span className="font-medium text-foreground">{user.email}</span>
                </p>
              )}
              <Button type="submit" className="w-full" disabled={sending || !subject.trim() || !message.trim()}>
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" /> : <Mail className="w-4 h-4 mr-2" aria-hidden="true" />}
                Send Message
              </Button>
            </form>
          )}

          <div className="flex items-center gap-4 p-6 rounded-2xl bg-card border border-border">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Clock className="w-6 h-6 text-primary" aria-hidden="true" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground">Response Time</h3>
              <p className="text-muted-foreground text-sm">We typically reply within 24 to 48 hours</p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
